import React from "react";
import {
  Inbox, AlertTriangle, MessageSquare, Calendar,
  Brain, ListChecks, Sparkles,
} from "lucide-react";
import RichBlogLayout from "../../components/RichBlogLayout";
import {
  Prose, PullQuote, MetricsRow, BeforeAfter, ProcessFlow,
  FeatureCallout, AgentCard, LiveFeed,
  ParallelGrid, SectionDivider,
} from "../../components/blog/BlogGraphics";

const BlogTrishaAutonomousMorningTriage = () => (
  <RichBlogLayout
    category="Practice Management"
    eyebrow="Practice Manager"
    title="Trisha: The AI Practice Manager That Triages Your Whole Weekend Before You Walk In"
    subtitle="47 inbound items between Friday night and Sunday — already worked, already routed, already prioritized by 7:52 AM."
    author="Asha Team"
    date="May 2, 2026"
    readTime="8 min read"
  >
    <Prose>
      <p>
        Monday, 7:48 AM. The practice manager unlocks the front door and
        flips on the lights. The phone has 11 voicemails. The fax tray has
        a stack of EOBs and prior auth approvals. The inbox has 28 emails.
        Two payer alerts. One patient pain report from Sunday afternoon.
        A cancellation from a patient that came in at 9:40 PM Saturday.
      </p>
      <p>
        The first hour and a half of Monday — across the entire team —
        will not be work. It will be sorting. Figuring out what's urgent.
        Figuring out who should handle what. Figuring out which of the 47
        inbound items happened to be duplicates, which were already
        handled, and which are silently on fire.
      </p>
      <p>
        That hour and a half is the most expensive hour and a half of the
        week. It's also the part of the week that an attentive team member
        could handle in their sleep — if they had eight extra hands.
      </p>
    </Prose>

    <AgentCard
      name="Trisha"
      role="AI Practice Manager · Triage + Delegation"
      initial="T"
      description="Trisha is the AI practice manager. She works through your full weekend backlog before anyone walks in, autonomously resolves what she can, and routes everything else to the right teammate with full context. By the time the team logs in, Monday has already been triaged."
      channels={["Voicemails", "Faxes", "Inbox", "Payer alerts", "Cancellations"]}
    />

    <Prose>
      <h2>The cost of an unmanaged Monday</h2>
      <p>
        Across a typical practice, the Friday-night-through-Sunday inbound
        flow is 30–60 distinct items. Each one needs to be opened, read,
        categorized, decided on, and either resolved or routed. At an
        average of 90 seconds per item — generous, given the context
        switching — that's a 45–90 minute morning of pure triage before the
        actual day begins.
      </p>
      <p>
        And that's the calm version. The chaotic version is what most
        teams actually experience: the triage and the patient phone calls
        happen at the same time, and one or both gets done badly.
      </p>
    </Prose>

    <MetricsRow
      items={[
        { value: 47, label: "Items resolved before 8 AM" },
        { value: 8, label: "Auto-resolved by Trisha" },
        { value: 0, label: "Minutes your team spent triaging" },
        { value: 7, suffix: ":52 AM", label: "Plan published, in your inbox" },
      ]}
    />

    <Prose>
      <p>
        Trisha is not another inbox. She is the one who reads your inboxes
        for you, makes the decisions she can make, and hands you back the
        ones she can't — already organized, already prioritized, already
        with context.
      </p>

      <h2>Routing is the floor; resolution is the bar</h2>
      <p>
        A lot of practice management software claims to "route tasks." In
        practice, that usually means a human still has to read the task,
        decide it's a task, and tag it. Trisha is different. She actually
        does the work she can do — and only the actual edge cases reach
        a human.
      </p>
    </Prose>

    <BeforeAfter
      beforeTitle="Monday morning, traditional"
      afterTitle="Monday morning, with Trisha"
      beforeItems={[
        "First 90 minutes spent sorting backlog instead of working it",
        "Urgent patient items buried under voicemail confirmations",
        "Duplicate prior auth approvals retyped or re-filed by hand",
        "Cancellations sit until someone notices the open chair",
      ]}
      afterItems={[
        "Backlog already triaged, prioritized, and assigned by 7:52 AM",
        "Pain report routed straight to the provider with full context",
        "Prior auth approvals matched and closed automatically",
        "Cancellations flagged for the scheduler with fill candidates",
      ]}
    />

    <Prose>
      <h2>How Trisha works the weekend backlog</h2>
      <p>
        Trisha doesn't sleep. From the moment your front door locks Friday
        evening through Monday at 7:52 AM, she's reading every inbound
        item the moment it lands. By the time anyone walks in, she has
        done four distinct jobs.
      </p>
    </Prose>

    <ProcessFlow
      steps={[
        {
          title: "Reads every inbound item the second it arrives",
          body:
            "Voicemails are transcribed. Faxes are classified. Emails and payer alerts are parsed. Each item is timestamped and tagged with intent.",
        },
        {
          title: "Resolves what she can, on her own",
          body:
            "Routine confirmations, duplicate prior auths, payer alerts that match an existing claim — Trisha closes them without asking the team to look.",
        },
        {
          title: "Identifies the highest-priority items",
          body:
            "Patient pain reports, denials with appeal windows closing, last-minute cancellations — these are tagged as priority and queued for the right teammate.",
        },
        {
          title: "Routes with context, not just a tag",
          body:
            "A denial doesn't just go to 'billing.' It goes to the right biller with the patient history, the prior submission, and a draft response already attached.",
        },
        {
          title: "Balances workload across the team",
          body:
            "The rest of the items are distributed evenly, so no single teammate walks in to a wall of 30 tickets while another has nothing.",
        },
        {
          title: "Publishes the plan by 7:52 AM",
          body:
            "Before the team unlocks the door, the morning is already structured. Each person opens to a clean, prioritized list of exactly what's theirs.",
        },
      ]}
    />

    <PullQuote>
      What normally takes a team over two hours of triage is already done.
      Your staff isn't sorting through chaos — they're stepping into a day
      that's already structured.
    </PullQuote>

    <Prose>
      <h2>The 47 items, live</h2>
      <p>
        Here is what Trisha worked through this weekend — every voicemail,
        every fax, every payer alert — between Friday at 5:14 PM and
        Monday at 7:52 AM. Eight items resolved autonomously. The rest
        organized, prioritized, and routed.
      </p>
    </Prose>

    <LiveFeed
      title="Weekend triage · 47 items processed"
      items={[
        { time: "Fri 5:42p", label: "Voicemail · Maria L. · reschedule request", detail: "Routed to scheduler · context attached", status: "done" },
        { time: "Fri 6:14p", label: "Fax · Delta EOB · $1,840", detail: "Auto-matched to claim · payment posted", status: "done" },
        { time: "Fri 8:11p", label: "Patient pain report · James T.", detail: "Priority · routed to Dr. Patel · context attached", status: "active" },
        { time: "Fri 9:33p", label: "Payer alert · Aetna fee schedule update", detail: "Reviewed · no action needed · filed", status: "done" },
        { time: "Sat 7:48a", label: "Voicemail · Anna P. · billing question", detail: "Routed to biller · ledger summary attached", status: "active" },
        { time: "Sat 9:40p", label: "Late cancellation · David R. · 9:00 AM Mon", detail: "Flagged for scheduler · 4 fill candidates", status: "active" },
        { time: "Sun 10:12a", label: "Fax · prior auth approval · D6010", detail: "Matched to existing request · auto-closed", status: "done" },
        { time: "Sun 11:55a", label: "Voicemail · duplicate confirm of Tue 3pm", detail: "Detected as duplicate · removed", status: "done" },
        { time: "Sun 2:14p", label: "Denial · Cigna · D2750 · waiting period", detail: "Priority · routed to Linda · draft appeal staged", status: "active" },
        { time: "Sun 3:02p", label: "Email · referral from Dr. Wong · new patient", detail: "Routed to scheduler · intake link queued", status: "active" },
        { time: "Sun 6:48p", label: "Voicemail · supply order confirmation", detail: "Auto-acknowledged · filed", status: "done" },
        { time: "Mon 7:52a", label: "Morning plan published · 47 items triaged", detail: "8 auto-resolved · 39 routed with context", status: "done" },
      ]}
    />

    <Prose>
      <h2>How the team's morning actually opens</h2>
      <p>
        Each teammate opens their morning to a clean list of items that
        are unambiguously theirs. The biller sees the denial and the
        draft appeal. The provider sees the pain report with patient
        history. The scheduler sees the cancellation with four fill
        candidates already identified. The hygiene coordinator sees a
        clean recall list with no duplicates.
      </p>
      <p>
        Nobody is asking "where do I start?" The starting points are
        already in front of them.
      </p>
    </Prose>

    <ParallelGrid
      title="Monday at 8:00 AM — what each teammate sees"
      columns={2}
      items={[
        { title: "Front desk", subtitle: "11 inbound · 0 unsorted", icon: Inbox, status: "active" },
        { title: "Billing", subtitle: "1 priority denial · draft appeal ready", icon: AlertTriangle, status: "active" },
        { title: "Scheduler", subtitle: "1 cancellation · 4 fill candidates", icon: Calendar, status: "active" },
        { title: "Provider", subtitle: "1 pain report · context attached", icon: MessageSquare, status: "active" },
        { title: "Hygiene coordinator", subtitle: "Clean recall list · no duplicates", icon: ListChecks, status: "done" },
        { title: "Practice manager", subtitle: "Daily plan in inbox · 7:52 AM", icon: Brain, status: "done" },
      ]}
    />

    <Prose>
      <h2>The compounding effect across a quarter</h2>
      <p>
        Save 90 minutes of triage every Monday and you've saved a full
        eight-hour workday per quarter. The bigger payoff is the
        second-order kind: the patient pain report that gets handled at
        8:01 AM instead of 9:35 AM. The denial appeal that goes out the
        same day instead of three days later. The cancellation that gets
        filled by 8:30 AM instead of leaving the chair empty.
      </p>
      <p>
        Triage is the unsexy backbone of practice management. Doing it
        well — and doing it without a human — is what makes the rest of
        the week possible.
      </p>
    </Prose>

    <FeatureCallout
      stat="2 hrs → 0"
      label="Of human triage, every Monday"
      description="Trisha works the full weekend backlog before anyone walks in. The team opens to a structured day, not a sorting exercise."
      icon={Sparkles}
    />

    <Prose>
      <h2>The practice manager's job, after Trisha</h2>
      <p>
        Practice managers stop being the triage layer and become the
        decision layer. The first 90 minutes of every Monday — the most
        cognitively expensive part of the week — disappears. What's left
        is the work itself: the patient calls, the case planning, the
        team coaching, the actual practice management.
      </p>
    </Prose>

    <SectionDivider label="Bottom line" />

    <Prose>
      <p>
        Mondays don't have to start in chaos. Trisha quietly works the
        weekend so the team starts the week ahead of it — already
        triaged, already prioritized, already moving.
      </p>
    </Prose>
  </RichBlogLayout>
);

export default BlogTrishaAutonomousMorningTriage;
